import { useListPreferences, type PlainUserPreference } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser, type PlainUser } from "@/queries/user-queries";
import { useAutoDeleteValues } from "@/hooks/use-auto-delete-values";
import { useOrganization } from "@/queries/organization-queries";
import { shouldShowUpdateNudge } from "@/version-tracker";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export const PRIVACY_POLICY_LAST_UPDATED_DATE = new Date("2025-12-02T00:00:00Z");

export enum NudgeKeys {
    "PRIVACY_POLICY_UPDATED" = "PRIVACY_POLICY_UPDATED",

    "AUTO_DELETE_POLICY" = "AUTO_DELETE_POLICY",
    "VERSION_UPDATE" = "VERSION_UPDATE",
    "AUTOMATIONS_LAUNCH" = "AUTOMATIONS_LAUNCH",
}

type Nudge =
    | { key: NudgeKeys.PRIVACY_POLICY_UPDATED }
    | { key: NudgeKeys.AUTO_DELETE_POLICY }
    | { key: NudgeKeys.VERSION_UPDATE }
    | { key: NudgeKeys.AUTOMATIONS_LAUNCH };

/**
 * Computes the active nudge (if any).
 * This ensures we only ever render one nudge at a time.
 */
export function useNudges(): { active?: Nudge; isPending: boolean; error: Error | null } {
    const { data: user, isPending: isPendingUser, error: errorUser } = useAuthenticatedUser();
    const { data: organization, isPending: isPendingOrg, error: errorOrg } = useOrganization();
    const { preferences, isPending: isPendingPreferences, error: errorPreferences } = useListPreferences();

    const { currentAutoDeletePolicyValue, previousAutoDeletePolicyValue } = useAutoDeleteValues();

    if (isPendingUser || isPendingOrg || isPendingPreferences) {
        return { isPending: true, error: null };
    }

    if (errorUser || errorOrg || errorPreferences) {
        return { isPending: false, error: errorUser || errorOrg || errorPreferences };
    }

    if (!user) {
        return { isPending: isPendingUser, error: errorUser };
    }

    if (!preferences) {
        return { isPending: isPendingPreferences, error: errorPreferences };
    }

    // Automations launch nudge (highest priority) — show to Core and FREE_ONA users
    const isCore = organization?.tier === OrganizationTier.CORE;
    const isFreeOna = organization?.tier === OrganizationTier.FREE_ONA;
    if ((isCore || isFreeOna) && !hasDismissedNudge(NudgeKeys.AUTOMATIONS_LAUNCH, preferences)) {
        return {
            active: { key: NudgeKeys.AUTOMATIONS_LAUNCH },
            isPending: false,
            error: null,
        };
    }

    // Enterprise orgs don't get privacy policy nudges
    const isEnterprise = organization?.tier === OrganizationTier.ENTERPRISE;
    if (!isEnterprise && !hasAcceptedCurrentPrivacyPolicy({ user, preferences })) {
        return {
            active: { key: NudgeKeys.PRIVACY_POLICY_UPDATED },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    // Check for auto-delete policy changes
    // Show nudge only if there was a previous value AND it differs from current value
    // For new users (no previous value), don't show the nudge
    if (previousAutoDeletePolicyValue !== undefined && previousAutoDeletePolicyValue !== currentAutoDeletePolicyValue) {
        return {
            active: { key: NudgeKeys.AUTO_DELETE_POLICY },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    // Version update nudge (lowest priority) — uses client-side state from version tracker
    if (shouldShowUpdateNudge()) {
        return {
            active: { key: NudgeKeys.VERSION_UPDATE },
            isPending: false,
            error: null,
        };
    }

    return { isPending: isPendingPreferences, error: errorPreferences };
}

/**
 * Returns true when the active nudge is VERSION_UPDATE.
 * Used by the sidebar update chip and dot indicators.
 */
export function useIsVersionUpdateAvailable(): boolean {
    const { active, isPending, error } = useNudges();
    if (isPending || error) {
        return false;
    }
    return active?.key === NudgeKeys.VERSION_UPDATE;
}

function hasDismissedNudge(key: string, preferences: PlainUserPreference[]): boolean {
    const savedPreference = preferences?.find((pref) => pref.key === key);
    if (!savedPreference?.value || savedPreference.value === "false") {
        return false;
    }

    return !!savedPreference?.value;
}

function hasAcceptedCurrentPrivacyPolicy(options: { user: PlainUser; preferences: PlainUserPreference[] }): boolean {
    const { user, preferences } = options;
    const preference = preferences?.find((pref) => pref.key === NudgeKeys.PRIVACY_POLICY_UPDATED.toString());

    // If the user was created after the last privacy policy update, we don't need to show the nudge
    if (user.createdAt?.seconds && user.createdAt?.seconds > PRIVACY_POLICY_LAST_UPDATED_DATE.getTime() / 1000) {
        return true;
    }

    // If the user has already accepted the current privacy policy, we don't need to show the nudge
    if (preference?.value) {
        const acceptedAt = new Date(preference?.value);
        if (acceptedAt > PRIVACY_POLICY_LAST_UPDATED_DATE) {
            return true;
        }
    }

    return false;
}
